// @ts-check
import { ref } from 'vue';

/**
 * Sjednocené načítání asynchronních dat pro komponenty.
 *
 * Drží `data`/`loading`/`error`, zavolá `loader`, přepne `loading` a chybu
 * uloží do `error` (NErethrowuje — komponenta ji čte jako reaktivní stav).
 * Každá komponenta dodá vlastní `loader` (jiná data, stejné technické řešení).
 *
 * Usage:
 *   const { data: regTypes, loading, error, load } =
 *     useResource(async () => (await get('/api/...')).items, { initial: [] });
 *   await load();            // error skončí v `error.value`, nevyhodí
 *   if (error.value) { ... } // volitelná reakce
 *
 * @template T
 * @param {(...args: any[]) => Promise<T>} loader  funkce vracející Promise s daty
 * @param {{ initial?: T | null }} [options]
 * @returns {{
 *   data: import('vue').Ref<T | null>,
 *   loading: import('vue').Ref<boolean>,
 *   error: import('vue').Ref<unknown>,
 *   load: (...args: any[]) => Promise<T | undefined>
 * }}
 */
export function useResource(loader, { initial = null } = {}) {
  const data = ref(initial);
  const loading = ref(false);
  const error = ref(null);

  async function load(...args) {
    loading.value = true;
    error.value = null;
    try {
      const result = await loader(...args);
      data.value = result;
      return result;
    } catch (e) {
      error.value = e;
      return undefined;
    } finally {
      loading.value = false;
    }
  }

  return { data, loading, error, load };
}
